// A room re-derived after an edit (docs/claude/rooms.md, *Regenerating a room*):
// its wall segments, its ceiling's setbacks, its benches against its partitions,
// the connections that lost their end, the holes its heaters carve, a steam
// room's fixtures, and every dimension anchored to one of its corners. DOM-free:
// main.ts's `regenerateSauna` hands it the drawing and flashes what it returns.
import type {
  Connection,
  Drawing,
  ProductInstance,
  ProductTemplate,
  Vec2,
  WallSide,
} from '../shared/types';
import { traitsOf } from '../shared/categories';
import {
  benchInterior,
  sideTotalDepth,
} from './benches';
import {
  type CeilingNote,
  ceilingShapeOf,
  fitSetbacks,
  hasCeiling,
  hasSetbacks,
} from './ceiling';
import { followVertices } from './anchors';
import { endOnGoneFeature } from './connections';
import { mainRoomDoor } from './door';
import { floorOccupants } from './floorOccupancy';
import { instanceToWorld } from './geometry';
import { clampGlassToDoor } from './glass';
import {
  autoAxisLocks,
  ensureWallSegments,
  isPolyRoom,
  mintFeatureIds,
  polyDoorChannelAt,
  polyFloorCentre,
  reconcileBenchesWithPartitions,
} from './polyRoom';

/** What the app knows that the room does not. */
export interface RoomEnv {
  /** Everything standing in the room, its fixtures included (main.ts `roomContents`). */
  contents: (room: ProductInstance) => ProductInstance[];
  /** The catalogue's entry for a placed part. */
  template: (id: string) => ProductTemplate | undefined;
  /** The room's setbacks as the gesture under way found them: each of its steps
   *  fits them from there. Absent outside a gesture. */
  setbacksFrom?: readonly number[];
  /** A fresh id for a pane, an opening or a partition laid without one. */
  newId?: (prefix: string) => string;
}

/** What a regeneration has to say: main.ts translates, flashes and deselects. */
export interface RoomRegen {
  /** What fitting the ceiling's setbacks found (null: nothing to say). */
  note: CeilingNote | null;
  /** The connections that went with a door or heater wall that is gone. */
  lost: Connection[];
}

/** Steam rooms: the clearance between the exterior wall and the door channel (mm). */
export const STEAM_DRAIN_GAP = 50;

/**
 * Re-derive everything that follows from a room, in order: a legacy rectangular
 * room becomes a polygon; the panes keep clear of the door; every pane / opening
 * owns its wall segment again (dimensions on its corners follow the renumbering)
 * and has its id;
 * a shaped ceiling's setbacks fit the room; benches meet the partitions; a
 * connection glued to a door or heater wall that is gone goes; the heaters carve
 * the benches; a steam room's drain and door channel follow. Idempotent.
 */
export function regenerateRoom(drawing: Drawing, room: ProductInstance, env: RoomEnv): RoomRegen {
  const out: RoomRegen = { note: null, lost: [] };
  if (!room.autoBenches) return out;
  if (!isPolyRoom(room)) migrateRectRoomToPoly(room);
  clampGlassToDoor(room);
  followVertices(drawing, room.id, () => ensureWallSegments(room));
  if (env.newId) mintFeatureIds(room, env.newId);
  if (hasCeiling(room) && hasSetbacks(ceilingShapeOf(room))) out.note = fitSetbacks(room, env.setbacksFrom);
  reconcileBenchesWithPartitions(room);
  out.lost = drawing.connections.filter((c) => endOnGoneFeature(drawing, c, room.id));
  if (out.lost.length) drawing.connections = drawing.connections.filter((c) => !out.lost.includes(c));
  const holes = heaterHoles(room, env);
  room.benchHoles = holes.length ? holes : undefined;
  if (room.category === 'steamroom') syncRoomFixtures(drawing, room);
  return out;
}


/** The footprints that carve the room's benches (a category that
 *  `carvesBenches`: heaters, a steam room's feature cube), each in the room's
 *  local frame. */
export function heaterHoles(sauna: ProductInstance, env: RoomEnv): Vec2[][] {
  return floorOccupants(sauna, env.contents(sauna))
    .filter((o) => traitsOf(o.part.category).carvesBenches && !env.template(o.part.templateId)?.noBenchCarve)
    .map((o) => o.outline);
}

/** A steam room's auto fixture of the given kind, if it exists. */
export function roomFixtureOf(drawing: Drawing, room: ProductInstance, kind: 'centerDrain' | 'doorChannel'): ProductInstance | undefined {
  return drawing.instances.find((i) => i.roomFixture?.roomId === room.id && i.roomFixture.kind === kind);
}


/**
 * Re-derive a steam room's fixtures from its current geometry: the floor drain
 * sits at the centre of the walkable surface (interior minus benches), and the
 * drain channel sits just outside the exterior wall in front of the door, its
 * length matching the door width. Idempotent — safe to call on every change.
 */
export function syncRoomFixtures(drawing: Drawing, room: ProductInstance): void {
  if (room.category !== 'steamroom') return;
  const poly = isPolyRoom(room);
  const drain = roomFixtureOf(drawing, room, 'centerDrain');
  if (drain) {
    // Rect: the bench-clear walkable centre. Poly: the inner-floor centroid.
    const centerLocal = poly ? polyFloorCentre(room) : roomWalkableCenter(room);
    const w = instanceToWorld(room, centerLocal);
    drain.x = w.x;
    drain.y = w.y;
    drain.rotation = room.rotation;
  }
  const channel = roomFixtureOf(drawing, room, 'doorChannel');
  const mainDoor = mainRoomDoor(room);
  if (channel && mainDoor) {
    channel.width = Math.max(50, mainDoor.width);
    let localCenter: Vec2;
    let rotDeg = room.rotation;
    if (poly) {
      const at = polyDoorChannelAt(room, STEAM_DRAIN_GAP + channel.depth / 2);
      localCenter = at?.at ?? { x: 0, y: 0 };
      if (at) rotDeg = (room.rotation + (Math.atan2(at.dir.y, at.dir.x) * 180) / Math.PI) % 360;
    } else {
      // Rect: front wall at local y = -depth/2; the channel sits just outside it.
      localCenter = { x: mainDoor.offset, y: -room.depth / 2 - STEAM_DRAIN_GAP - channel.depth / 2 };
    }
    const w = instanceToWorld(room, localCenter);
    channel.x = w.x;
    channel.y = w.y;
    channel.rotation = rotDeg;
  }
}

/**
 * Centre (local mm) of a room's walkable floor: the interior with the room's
 * ACTUAL bench bands removed (back/left/right; the front/door wall never has
 * benches). With no benches this is just the interior centre.
 */
function roomWalkableCenter(inst: ProductInstance): Vec2 {
  const { ix0, ix1, iy0, iy1 } = benchInterior(inst);
  const xLeft = ix0 + sideTotalDepth(inst, 'left');
  const xRight = ix1 - sideTotalDepth(inst, 'right');
  const yBack = iy1 - sideTotalDepth(inst, 'back');
  return { x: (xLeft + xRight) / 2, y: (iy0 + yBack) / 2 };
}

/**
 * Convert a legacy rectangular auto-bench room (width/depth, no `polygon`) into
 * an equivalent 4-vertex polygonal room in place, so every room runs on the one
 * polygon model. The footprint becomes edge 0 = front (door) … 1 = right …
 * 2 = back … 3 = left. Door, glass, openings, per-side benches and inner-wall
 * benches are re-anchored to those edges (a wall's along-coordinate maps to a
 * distance from the edge's first vertex). No-op for rooms already polygonal.
 */
export function migrateRectRoomToPoly(inst: ProductInstance): void {
  if (!inst.autoBenches || isPolyRoom(inst)) return;
  const hw = inst.width / 2, hd = inst.depth / 2;
  // CCW (Y-up); edge 0 is the front (min-y) wall so the door lands on it.
  inst.polygon = [{ x: -hw, y: -hd }, { x: hw, y: -hd }, { x: hw, y: hd }, { x: -hw, y: hd }];
  inst.polygonConstraints = autoAxisLocks(inst.polygon);
  const sideEdge = { front: 0, right: 1, back: 2, left: 3 } as const;
  // Distance from an edge's first vertex to a point at wall-along coordinate `c`.
  const alongOn = (wall: WallSide, c: number): number =>
    wall === 'front' ? c + hw : wall === 'right' ? c + hd : wall === 'back' ? hw - c : hd - c;
  const mapSpan = (wall: WallSide, s: number, e: number): { start: number; end: number } => {
    const a = alongOn(wall, s), b = alongOn(wall, e);
    return { start: Math.min(a, b), end: Math.max(a, b) };
  };
  for (const g of inst.glass || []) if (g.edge == null) { const m = mapSpan(g.wall, g.start, g.end); g.edge = sideEdge[g.wall]; g.start = m.start; g.end = m.end; }
  for (const o of inst.openings || []) if (o.edge == null) { const m = mapSpan(o.wall, o.start, o.end); o.edge = sideEdge[o.wall]; o.start = m.start; o.end = m.end; }
  // Per-side bench tiers → per-edge poly benches (fields are otherwise identical).
  const pb = inst.polyBenches ?? [];
  for (const side of ['back', 'left', 'right'] as const) {
    for (const leg of inst.benchTiers?.[side] ?? []) {
      const m = mapSpan(side, leg.start, leg.end);
      pb.push({ ...leg, edge: sideEdge[side], start: m.start, end: m.end });
    }
  }
  if (pb.length) inst.polyBenches = pb;
  inst.benchTiers = undefined;
  // Interior partition walls: anchor to the host edge; on a reversed edge
  // (back/left run opposite the wall coordinate) the two bench faces swap.
  for (const iw of inst.innerWalls || []) {
    if (iw.edge != null) continue;
    iw.edge = sideEdge[iw.wall];
    iw.pos = alongOn(iw.wall, iw.pos);
    if ((iw.wall === 'back' || iw.wall === 'left') && iw.benches) {
      const t = iw.benches.a; iw.benches.a = iw.benches.b; iw.benches.b = t;
    }
  }
}
